import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { KeyRound, Loader2, Info, AlertTriangle, CreditCard } from "lucide-react";
import api from "../../api/api";
import { Alert, EmptyState, Field, Panel, Pill, Stat, apiError, formatDate, formatMonth, useOrgStyles } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";
import { seatPrice, seatTotal } from "../../utils/orgPricing";
import { InviteCode } from "./OrgPrograms";
import { EmailText } from "./OrgStudents";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
/** Seats bill on the tier's own cycle. */
const perPeriod = (billingMode) => (billingMode === "yearly" ? "per year" : "per month");

/** Same rule as the backend: the biggest discount step the quantity reaches
 *  applies to every seat. (Also used by Admin → Organizations.) */
export function quote(pricing, qty) {
    let pct = 0;
    (pricing?.Discounts || []).forEach((d) => { if (qty >= d.MinQuantity) pct = d.PercentOff; });
    const each = seatPrice(pricing?.UnitPrice, pct);
    return { pct, each, total: seatTotal(each, qty) };
}
const pctText = (pct) => `${Math.round(Number(pct) * 100) / 100}%`;
/** Licenses run by calendar month: a plan that stops renewing keeps its
 *  licenses through the end of the month its paid period ends in.
 *  (Also used by the Overview and Admin → Organizations.) */
export function monthEnd(value) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
    return last.toISOString().slice(0, 10);
}

/** A student in the assign dropdown and in license rows: e<RosterEntryID>, or
 *  c<ClientID> for a member without a roster entry (and licenses from before codes). */
const studentKey = (x) => (x.RosterEntryID ? `e${x.RosterEntryID}` : `c${x.ClientID}`);
const studentOptionLabel = (m) => {
    const email = m.Email || m.EmailAddress;
    return `${m.LastName}, ${m.FirstName}${m.RosterID ? ` · ${m.RosterID}` : ""}${email ? ` (${email})` : ""}`
        + (m.Status === "invited" ? " · not joined yet" : "");
};

const REQUEST_STATUS = {
    pending: { label: "Waiting for approval", tone: "warning" },
    approved: { label: "Approved · waiting for payment", tone: "primary" },
    active: { label: "Active", tone: "success" },
    merged: { label: "Added to plan", tone: "success" },
    ended: { label: "Ended", tone: "neutral" },
    rejected: { label: "Declined", tone: "danger" },
    cancelled: { label: "Cancelled", tone: "neutral" },
};

/**
 * Monthly seats. A license is valid from the 1st to the end of the month and
 * renews automatically. A removed license's seat stays used until the month
 * ends, so it can't be handed to someone else until the 1st.
 */
export default function OrgLicenses() {
    //old const { orgId, reloadOrg } = useOrgPortal();
    // Only Build My Biz Brand admins change license counts and renewal (org admins still pay)
    const { orgId, reloadOrg, isPlatformAdmin } = useOrgPortal();
    const s = useOrgStyles();
    const [summary, setSummary] = useState(null);
    const [staff, setStaff] = useState([]);
    const [roster, setRoster] = useState([]);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [coachPick, setCoachPick] = useState("");
    const [studentPick, setStudentPick] = useState("");
    const [requests, setRequests] = useState({ pricing: {}, requests: [] });
    const [notice, setNotice] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();

    const load = useCallback(async () => {
        try {
            const [lic, st, ro] = await Promise.all([
                api.get(`/org/${orgId}/licenses`),
                api.get(`/org/${orgId}/staff`),
                api.get(`/org/${orgId}/roster?sort=alpha`),
            ]);
            setSummary(lic.data);
            setStaff(st.data.staff || []);
            //old setRoster(ro.data.students || []);
            setRoster(ro.data.roster || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load licenses."));
        }
    }, [orgId]);

    // License requests load on their own so the rest of the page still works without them.
    const loadRequests = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/license-requests`);
            setRequests({ pricing: res.data.pricing || {}, requests: res.data.requests || [] });
        } catch { /* keep the page usable */ }
    }, [orgId]);

    useEffect(() => { load(); loadRequests(); }, [load, loadRequests]);

    // Back from Stripe (?license_session=...): confirm the payment so the seats
    // show up now, even if the webhook hasn't arrived yet.
    useEffect(() => {
        const sessionId = searchParams.get("license_session");
        if (!sessionId) return;
        const next = new URLSearchParams(searchParams);
        next.delete("license_session");
        setSearchParams(next, { replace: true });
        api.post(`/org/${orgId}/license-requests/confirm`, { sessionId })
            .then(() => {
                setNotice("Payment received. Your new licenses are ready to assign.");
                return Promise.all([load(), loadRequests(), reloadOrg()]);
            })
            .catch((err) => setError(apiError(err, "We couldn't confirm the payment yet. Refresh in a minute.")));
    }, [searchParams, setSearchParams, orgId, load, loadRequests, reloadOrg]);

    const licensedCoaches = useMemo(() => new Set((summary?.licenses || []).filter((l) => l.LicenseType === "coach" && l.IsActive).map((l) => l.AdminID)), [summary]);
    //old const licensedStudents = useMemo(() => new Set((summary?.licenses || []).filter((l) => l.LicenseType === "student" && l.IsActive).map((l) => l.ClientID)), [summary]);
    // Someone whose license was removed earlier this month can get it back
    // without a new seat (the backend reuses their seat).
    //old const reusable = useMemo(() => new Set((summary?.licenses || []).filter((l) => !l.IsActive)
    //old     .map((l) => `${l.LicenseType}:${l.AdminID || l.ClientID}`)), [summary]);
    // Student licenses belong to roster entries now (c<ClientID> for a license from before codes)
    const reusable = useMemo(() => new Set((summary?.licenses || []).filter((l) => !l.IsActive)
        .map((l) => (l.LicenseType === "coach" ? `coach:${l.AdminID}` : `student:${studentKey(l)}`))), [summary]);
    const coachOptions = staff.filter((m) => m.IsCoach && !licensedCoaches.has(m.AdminID));
    //old const studentOptions = roster.filter((m) => !licensedStudents.has(m.ClientID));
    // Everyone on the roster without a license this month, invited or joined
    const studentOptions = roster.filter((m) => !m.HasLicense);

    // Returns true when it worked, so a form can stay open (and keep what was
    // typed) when it didn't.
    const act = async (fn) => {
        setBusy(true);
        setError("");
        try {
            await fn();
            await Promise.all([load(), reloadOrg()]);
            return true;
        } catch (err) {
            setError(apiError(err));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const assign = (type, id) => act(async () => {
        //old await api.post(`/org/${orgId}/licenses`, type === "coach" ? { licenseType: "coach", adminId: Number(id) } : { licenseType: "student", clientId: Number(id) });
        // Students are picked as e<RosterEntryID>, or c<ClientID> for a member without an entry
        await api.post(`/org/${orgId}/licenses`, type === "coach" ? { licenseType: "coach", adminId: Number(id) }
            : id.startsWith("e") ? { licenseType: "student", rosterEntryId: Number(id.slice(1)) }
                : { licenseType: "student", clientId: Number(id.slice(1)) });
        if (type === "coach") setCoachPick(""); else setStudentPick("");
    });

    const requestAct = (fn) => act(async () => { await fn(); await loadRequests(); });
    const createRequest = (kind, quantity, note) => requestAct(async () => {
        await api.post(`/org/${orgId}/license-requests`, { licenseType: kind, quantity, note });
        setNotice("Request sent. You'll be able to pay here once Build My Biz Brand approves it.");
    });
    const cancelRequest = (req) => {
        if (window.confirm(`Cancel your request for ${req.Quantity} ${req.LicenseType} licenses?`)) {
            requestAct(() => api.delete(`/org/${orgId}/license-requests/${req.RequestID}`));
        }
    };
    const payRequest = async (req) => {
        setBusy(true);
        setError("");
        try {
            const res = await api.post(`/org/${orgId}/license-requests/${req.RequestID}/checkout`);
            if (res.data.checkoutUrl) {
                window.location.assign(res.data.checkoutUrl);
                return;
            }
            // Added to the existing plan (charged on the card on file), or a
            // checkout that had already gone through was confirmed
            setNotice(res.data.addedToPlan
                ? `${req.Quantity} ${req.LicenseType} licenses added to your plan. They're ready to assign.`
                : "Payment received. Your licenses are ready to assign.");
            await Promise.all([load(), loadRequests(), reloadOrg()]);
        } catch (err) {
            setError(apiError(err, "Couldn't take the payment."));
        }
        setBusy(false);
    };

    // Fewer seats from the next renewal (or back to today's number)
    const changeSeats = (plan, seats) => requestAct(async () => {
        await api.post(`/org/${orgId}/license-requests/${plan.RequestID}/seats`, { seats });
        setNotice(seats === plan.Quantity
            ? "Your plan keeps its current number of licenses."
            : `From ${plan.CurrentPeriodEnd ? formatDate(plan.CurrentPeriodEnd) : "your next renewal"}, your plan has ${seats} licenses.`);
    });

    const renewRequest = async (req) => {
        setBusy(true);
        setError("");
        try {
            const res = await api.post(`/org/${orgId}/license-requests/${req.RequestID}/renew`);
            if (res.data.checkoutUrl) {
                window.location.assign(res.data.checkoutUrl);
                return;
            }
            setNotice("Renewed. Your licenses carry on next month.");
            await Promise.all([load(), loadRequests(), reloadOrg()]);
        } catch (err) {
            setError(apiError(err, "Couldn't start the renewal."));
        }
        setBusy(false);
    };
    const expiring = requests.requests.filter((r) => r.ExpiresThisMonth);
    // Licenses ending with this month because next month has fewer seats
    const endingLicenses = (summary?.licenses || []).filter((l) => l.IsActive && l.EndPeriod);
    // The org's plan per license type: the active row with automatic payment
    const plans = {
        coach: requests.requests.find((r) => r.LicenseType === "coach" && r.Status === "active" && r.AutoRenews),
        student: requests.requests.find((r) => r.LicenseType === "student" && r.Status === "active" && r.AutoRenews),
    };
    // Stop / restart automatic payment. Stopping keeps the licenses until the
    // paid period ends.
    const setAutoRenew = (req, renew) => {
        const ends = req.CurrentPeriodEnd ? formatDate(monthEnd(req.CurrentPeriodEnd)) : "the end of the paid period";
        const ok = renew || window.confirm(
            `Stop renewing ${req.Quantity} ${req.LicenseType} licenses?\n\nYou won't be charged again. You keep the licenses until ${ends}, then they end.`
            + (req.CurrentPeriodEnd ? ` You can turn renewal back on until ${formatDate(req.CurrentPeriodEnd)}.` : ""));
        if (!ok) return;
        requestAct(async () => {
            await api.post(`/org/${orgId}/license-requests/${req.RequestID}/auto-renew`, { renew });
            setNotice(renew ? "Automatic renewal is back on." : `Renewal stopped. The licenses last until ${ends}.`);
        });
    };

    const revoke = (lic) => {
        const ok = window.confirm(
            `Remove ${lic.FirstName} ${lic.LastName}'s license?\n\nThey lose access now. The seat stays used until ${formatMonth(summary.nextPeriod)} begins, so it can't go to someone else this month.`
        );
        if (ok) act(() => api.delete(`/org/${orgId}/licenses/${lic.LicenseID}`));
    };

    if (!summary) {
        return error ? <Alert>{error}</Alert> : <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>;
    }

    const pools = [
        { key: "coach", title: "Coach licenses", pool: summary.coach, options: coachOptions, pick: coachPick, setPick: setCoachPick,
          optionLabel: (m) => `${m.FirstName} ${m.LastName}`, optionValue: (m) => m.AdminID,
          emptyHint: "Add coaches under Coaches & Admins first." },
        { key: "student", title: "Student licenses", pool: summary.student, options: studentOptions, pick: studentPick, setPick: setStudentPick,
          //old optionLabel: (m) => `${m.LastName}, ${m.FirstName} (${m.EmailAddress})`, optionValue: (m) => m.ClientID,
          optionLabel: studentOptionLabel, optionValue: studentKey,
          emptyHint: "Every student on your roster already has a license." },
    ];

    return (
        <div className="space-y-6">
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
            {endingLicenses.length > 0 && (
                <div role="status" className={`flex items-start gap-2 rounded-2xl border p-4 text-sm ${s.dark ? "border-amber-800 bg-amber-900/20" : "border-amber-200 bg-amber-50"}`}>
                    <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-amber-500" />
                    <p className={s.text}>
                        <strong>{endingLicenses.length} license{endingLicenses.length === 1 ? "" : "s"}</strong> end on <strong>{formatDate(summary.periodEnd)}</strong> because
                        you'll have fewer licenses next month (marked below). To keep {endingLicenses.length === 1 ? "it" : "them"}, renew or get more licenses,
                        or remove someone else: the freed license goes back to them.
                    </p>
                </div>
            )}
            {expiring.map((r) => (
                <div key={r.RequestID} role="status"
                     className={`flex flex-col gap-3 rounded-2xl border p-4 text-sm sm:flex-row sm:items-center sm:justify-between ${s.dark ? "border-amber-800 bg-amber-900/20" : "border-amber-200 bg-amber-50"}`}>
                    <p className={`flex items-start gap-2 ${s.text}`}>
                        <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-amber-500" />
                        <span>
                            The <strong>{r.ScheduledQuantity ?? r.Quantity} {r.LicenseType} licenses</strong> you bought end on <strong>{formatDate(r.PeriodEnd)}</strong>.
                            {" "}Renew to keep them next month{r.Status === "ended" ? " (the automatic payment stopped)" : ""}.
                        </span>
                    </p>
                    <button type="button" className={s.btnPrimary} disabled={busy} onClick={() => renewRequest(r)}>
                        <CreditCard size={14} /> Renew
                    </button>
                </div>
            ))}
            <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${s.subPanel}`}>
                <Info size={18} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                <p className={s.body}>
                    <strong className={s.text}>{formatMonth(summary.period)}</strong>: licenses run through {formatDate(summary.periodEnd)} and renew automatically.
                    A license you remove can't be given to someone else until {formatDate(summary.nextPeriod)}.
                </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {pools.map(({ key, title, pool, options, pick, setPick, optionLabel, optionValue, emptyHint }) => (
                    <Panel key={key} title={title}>
                        <div className="grid grid-cols-3 gap-3">
                            <Stat label="Assignments this month" value={`${pool.used} / ${pool.limit}`} tone={pool.limit > 0 && pool.available === 0 ? "warn" : "default"}
                                  hint={pool.purchased > 0 ? `incl. ${pool.purchased} added` : undefined} />
                            <Stat label="Available" value={pool.available} />
                            <Stat label="Next month" value={pool.availableNextMonth} hint="available" />
                        </div>
                        {pool.limit > 0 && pool.available === 0 && (
                            <p className={`mt-4 flex items-start gap-2 text-sm ${s.text}`}>
                                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-500" />
                                <span>
                                    All {key} licenses are in use this month.
                                    {key === "student" && studentOptions.length > 0 && ` ${studentOptions.length} student${studentOptions.length === 1 ? " on your roster doesn't" : "s on your roster don't"} have one.`}
                                </span>
                            </p>
                        )}
                        {pool.limit === 0 ? (
                            <p className={`mt-4 text-sm ${s.muted}`}>Your plan doesn't include {key} licenses yet.</p>
                        ) : (
                            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                <select aria-label={`Assign a ${key} license`} className={s.input} value={pick} onChange={(e) => setPick(e.target.value)}
                                        disabled={options.length === 0}>
                                    <option value="">
                                        {options.length === 0 ? emptyHint : pool.available === 0 ? "No new licenses left this month" : `Choose a ${key}…`}
                                    </option>
                                    {options.map((m) => {
                                        const canReuse = reusable.has(`${key}:${optionValue(m)}`);
                                        return (
                                            <option key={optionValue(m)} value={optionValue(m)} disabled={pool.available === 0 && !canReuse}>
                                                {optionLabel(m)}{canReuse ? " (restore this month's license)" : ""}
                                            </option>
                                        );
                                    })}
                                </select>
                                <button type="button" className={s.btnPrimary}
                                        disabled={!pick || busy || (pool.available === 0 && !reusable.has(`${key}:${pick}`))}
                                        onClick={() => assign(key, pick)}>
                                    Assign
                                </button>
                            </div>
                        )}
                        <Plan plan={plans[key]} pricing={requests.pricing[key]} busy={busy} canEdit={isPlatformAdmin}
                              onChangeSeats={changeSeats} onAutoRenew={setAutoRenew} />
                        <RequestMore kind={key} pricing={requests.pricing[key]} busy={busy} plan={plans[key]} canRequest={isPlatformAdmin}
                                     open={requests.requests.find((r) => r.LicenseType === key && (r.Status === "pending" || r.Status === "approved"))}
                                     onCreate={createRequest} onCancel={cancelRequest} onPay={payRequest} />
                    </Panel>
                ))}
            </div>

            {requests.requests.length > 0 && (
                <Panel title="License requests" padded={false}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                    <th className={s.th}>Requested</th>
                                    <th className={s.th}>Licenses</th>
                                    <th className={s.th}>Price</th>
                                    <th className={s.th}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.requests.map((r) => (
                                    <tr key={r.RequestID} className={`border-b last:border-0 ${s.row}`}>
                                        <td className={`${s.td} whitespace-nowrap`}>{formatDate(r.CreatedOn)}</td>
                                        <td className={s.td}>
                                            {r.Quantity} {r.LicenseType === "coach" ? "coach" : "student"}
                                            {r.RenewsRequestID && <span className={`ml-1 text-xs ${s.muted}`}>(renewal)</span>}
                                        </td>
                                        <td className={`${s.td} whitespace-nowrap tabular-nums`}>
                                            {r.TotalAmount != null ? <>{money(r.TotalAmount)} {perPeriod(r.BillingMode)}</> : <span className={s.muted}>Set by Build My Biz Brand</span>}
                                            {r.ScheduledQuantity != null && (
                                                <div className={`text-xs ${s.muted}`}>
                                                    {r.Status === "active" ? `from next renewal (${r.ScheduledQuantity} licenses)` : `for ${r.ScheduledQuantity} licenses`}
                                                </div>
                                            )}
                                        </td>
                                        <td className={s.td}>
                                            <Pill tone={(REQUEST_STATUS[r.Status] || {}).tone}>{(REQUEST_STATUS[r.Status] || {}).label || r.Status}</Pill>
                                            {r.Status === "rejected" && r.ReviewNote && <div className={`mt-1 text-xs ${s.muted}`}>{r.ReviewNote}</div>}
                                            {(r.Status === "active" || r.Status === "ended") && r.EndPeriod && <div className={`mt-1 text-xs ${s.muted}`}>Through {formatMonth(r.EndPeriod)}</div>}
                                            {r.Status === "active" && r.StartPeriod && !r.EndPeriod && r.RenewsRequestID && <div className={`mt-1 text-xs ${s.muted}`}>From {formatMonth(r.StartPeriod)}</div>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            )}

            <Panel title={`Licenses this month (${summary.licenses.length})`} padded={false}>
                {summary.licenses.length === 0 ? (
                    <EmptyState icon={KeyRound} title="No licenses assigned this month" body="Assign coach and student licenses above." />
                ) : (
                    <div className="relative overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                    <th className={s.th}>Name</th>
                                    <th className={s.th}>Type</th>
                                    <th className={s.th}>Code</th>
                                    <th className={s.th}>Assigned</th>
                                    <th className={s.th}>Status</th>
                                    <th className={s.th}><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.licenses.map((l) => (
                                    <tr key={l.LicenseID} className={`border-b last:border-0 ${s.row}`}>
                                        <td className={s.td}>
                                            <div className={`font-semibold ${s.text}`}>{l.FirstName} {l.LastName}</div>
                                            {/* old <div className={`text-xs ${s.muted}`}>{l.Email}</div> */}
                                            <div className={`text-xs ${s.muted} [overflow-wrap:anywhere]`}>
                                                {l.RosterID}{l.RosterID && (l.Email || l.EmailAddress) ? " · " : ""}
                                                {(l.Email || l.EmailAddress) && <EmailText email={l.Email || l.EmailAddress} />}
                                            </div>
                                        </td>
                                        <td className={s.td}><Pill tone={l.LicenseType === "coach" ? "primary" : "neutral"}>{l.LicenseType === "coach" ? "Coach" : "Student"}</Pill></td>
                                        <td className={s.td}>
                                            {!l.LicenseCode ? <span className={s.muted}>—</span> : (
                                                <div className="flex flex-col items-start gap-1">
                                                    {/* a removed license's code no longer works, so there's nothing to copy */}
                                                    {l.IsActive
                                                        ? <span className="whitespace-nowrap"><InviteCode code={l.LicenseCode} /></span>
                                                        : <span className={`whitespace-nowrap font-mono text-sm line-through ${s.muted}`}>{l.LicenseCode}</span>}
                                                    {!!l.IsActive && <span className={`text-xs ${s.muted}`}>{l.LicenseRedeemed ? "Joined" : "Not redeemed yet"}</span>}
                                                </div>
                                            )}
                                        </td>
                                        <td className={`${s.td} whitespace-nowrap`}>{formatDate(l.AssignedOn)}</td>
                                        <td className={s.td}>
                                            {!l.IsActive
                                                ? <Pill tone="warning">Removed · seat frees {formatDate(summary.nextPeriod)}</Pill>
                                                : l.EndPeriod
                                                    ? <Pill tone="warning">Ends {formatDate(summary.periodEnd)} · not enough licenses next month</Pill>
                                                    : <Pill tone="success">Active</Pill>}
                                        </td>
                                        <td className={`${s.td} text-right`}>
                                            {!!l.IsActive && <button type="button" className={s.btnDanger} disabled={busy} onClick={() => revoke(l)}>Remove</button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
}


/**
 * "Request more licenses": a Build My Biz Brand admin asks for seats here (or
 * sets them up from Admin → Organizations), then the org admin pays here. Price
 * comes from the group pricing on the tier their licenses give (set in
 * Membership Management → Edit Tier). Org admins can't ask for a number
 * themselves; they still pay for or cancel an open request.
 */
function RequestMore({ kind, pricing, plan, open, busy, canRequest, onCreate, onCancel, onPay }) {
    const s = useOrgStyles();
    const [show, setShow] = useState(false);
    const [qty, setQty] = useState(5);
    const [note, setNote] = useState("");
    const quantity = Math.max(1, Math.min(1000, parseInt(qty, 10) || 0));
    // With a plan, the discount follows the plan's new total (every seat gets it)
    const planSeats = plan ? (plan.ScheduledQuantity ?? plan.Quantity) : 0;
    const q = pricing ? quote(pricing, planSeats + quantity) : null;
    const period = perPeriod(pricing?.BillingMode);

    if (open) {
        return (
            <div className={`mt-4 rounded-xl border p-3 text-sm ${s.subPanel}`}>
                {open.Status === "pending" ? (
                    <p className={s.body}>Your request for <strong className={s.text}>{open.Quantity}</strong> {kind} licenses is waiting for Build My Biz Brand's approval.</p>
                ) : open.AddsToPlan ? (
                    <p className={s.body}>
                        Approved: <strong className={s.text}>{open.Quantity}</strong> more {kind} licenses
                        {open.TotalAmount != null && <> (+<strong className={s.text}>{money(open.TotalAmount)}</strong> {perPeriod(open.BillingMode)})</>}.
                        {" "}Adding them charges the card on your plan for the rest of this billing period; after that they're billed with your plan.
                    </p>
                ) : (
                    <p className={s.body}>
                        Approved: <strong className={s.text}>{open.Quantity}</strong> {kind} licenses
                        {open.TotalAmount != null && <> for <strong className={s.text}>{money(open.TotalAmount)}</strong> {perPeriod(open.BillingMode)}</>}.
                    </p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                    {open.Status === "approved" && (
                        <button type="button" className={s.btnPrimary} disabled={busy} onClick={() => onPay(open)}>
                            <CreditCard size={14} /> {open.AddsToPlan ? "Add to my plan" : "Pay now"}
                        </button>
                    )}
                    <button type="button" className={s.btnSecondary} disabled={busy} onClick={() => onCancel(open)}>Cancel request</button>
                </div>
            </div>
        );
    }

    if (!canRequest) {
        return <p className={`mt-4 text-sm ${s.muted}`}>Build My Biz Brand sets up your licenses. Contact us to change them.</p>;
    }

    if (!show) {
        return (
            <button type="button" className={`mt-4 ${s.btnLink}`} onClick={() => setShow(true)}>
                Need more {kind} licenses?
            </button>
        );
    }

    return (
        <form className={`mt-4 space-y-3 rounded-xl border p-3 ${s.subPanel}`}
              onSubmit={async (e) => {
                  e.preventDefault();
                  if (await onCreate(kind, quantity, note)) { setShow(false); setNote(""); }
              }}>
            <Field label="How many more?" htmlFor={`req-${kind}`}>
                <input id={`req-${kind}`} type="number" min="1" max="1000" className={s.input} value={qty} onChange={(e) => setQty(e.target.value)} />
            </Field>
            {q && plan ? (
                <p className={`text-sm ${s.body}`}>
                    Your plan goes from {planSeats} to <strong className={s.text}>{planSeats + quantity}</strong> licenses at{" "}
                    {money(q.each)} each{q.pct > 0 && <> ({pctText(q.pct)} off)</>}: <strong className={s.text}>{money(q.total)}</strong> {period}.
                    <span className={`block text-xs ${s.muted}`}>When you add them, you pay for the rest of this billing period only.</span>
                    {pricing.Discounts.length > 0 && (
                        <span className={`block text-xs ${s.muted}`}>
                            Volume discounts: {pricing.Discounts.map((d) => `${d.MinQuantity}+ = ${pctText(d.PercentOff)} off`).join(" · ")}
                        </span>
                    )}
                </p>
            ) : q ? (
                <p className={`text-sm ${s.body}`}>
                    {money(pricing.UnitPrice)} per license{q.pct > 0 && <>, <strong className={s.text}>{pctText(q.pct)} off</strong> for {quantity}</>}:{" "}
                    <strong className={s.text}>{money(q.total)}</strong> {period}.
                    {pricing.Discounts.length > 0 && (
                        <span className={`block text-xs ${s.muted}`}>
                            Volume discounts: {pricing.Discounts.map((d) => `${d.MinQuantity}+ = ${pctText(d.PercentOff)} off`).join(" · ")}
                        </span>
                    )}
                </p>
            ) : (
                <p className={`text-sm ${s.muted}`}>Build My Biz Brand will confirm the price when they review your request.</p>
            )}
            <Field label="Note (optional)" htmlFor={`req-note-${kind}`}>
                <textarea id={`req-note-${kind}`} rows={2} maxLength={500} className={s.input} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <p className={`text-xs ${s.muted}`}>
                {pricing
                    ? `Nothing is charged now. Once your request is approved, you pay here and the licenses are added right away. They renew automatically ${period.replace("per ", "every ")}.`
                    : "Nothing is charged here. Once Build My Biz Brand approves your request, the licenses are added right away."}
            </p>
            <div className="flex gap-2">
                <button type="submit" className={s.btnPrimary} disabled={busy}>Send request</button>
                <button type="button" className={s.btnSecondary} onClick={() => setShow(false)}>Cancel</button>
            </div>
        </form>
    );
}


/**
 * The org's plan for a license type, like a team plan in other apps: seats
 * renew automatically; fewer seats take effect at the next renewal; renewal
 * can be stopped (the paid period keeps its seats). Only Build My Biz Brand
 * admins (canEdit) change the seats or renewal; org admins see the plan.
 */
function Plan({ plan, pricing, busy, canEdit, onChangeSeats, onAutoRenew }) {
    const s = useOrgStyles();
    const [editing, setEditing] = useState(false);
    const [seats, setSeats] = useState("");
    if (!plan) return null;
    const next = plan.ScheduledQuantity ?? plan.Quantity;
    const renewsOn = plan.CurrentPeriodEnd ? formatDate(plan.CurrentPeriodEnd) : null;
    // The saved price is what the next renewal bills. With fewer seats scheduled,
    // this period's price is for today's number of seats.
    const scheduled = plan.ScheduledQuantity != null;
    const nowQuote = scheduled && pricing ? quote(pricing, plan.Quantity) : null;

    return (
        <div className={`mt-4 rounded-xl border p-3 text-sm ${s.subPanel}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className={`font-semibold ${s.text}`}>Your plan: {plan.Quantity} licenses</span>
                {nowQuote ? (
                    <span className={s.body}>{money(nowQuote.total)} {perPeriod(plan.BillingMode)}{nowQuote.pct > 0 && ` (${pctText(nowQuote.pct)} off)`}</span>
                ) : !scheduled && (
                    <span className={s.body}>{money(plan.TotalAmount)} {perPeriod(plan.BillingMode)}{Number(plan.PercentOff) > 0 && ` (${pctText(plan.PercentOff)} off)`}</span>
                )}
            </div>
            <p className={`mt-1 text-xs ${s.muted}`}>
                {plan.CancelAtPeriodEnd
                    ? (renewsOn
                        ? `Renewal is off: the licenses end on ${formatDate(monthEnd(plan.CurrentPeriodEnd))}. `
                            + (canEdit ? `Turn it back on by ${renewsOn} to keep them.` : `To keep them, contact Build My Biz Brand by ${renewsOn}.`)
                        : "Renewal is off: the licenses end when this period ends.")
                    : `Renews automatically${renewsOn ? ` on ${renewsOn}` : ""}.`}
                {scheduled && !plan.CancelAtPeriodEnd &&
                    ` From then on: ${plan.ScheduledQuantity} licenses, ${money(plan.TotalAmount)} ${perPeriod(plan.BillingMode)}${Number(plan.PercentOff) > 0 ? ` (${pctText(plan.PercentOff)} off)` : ""}.`}
            </p>
            {!canEdit ? null : editing ? (
                <form className="mt-2 flex flex-wrap items-center gap-2"
                      onSubmit={(e) => { e.preventDefault(); onChangeSeats(plan, Number(seats)); setEditing(false); }}>
                    <label htmlFor={`seats-${plan.RequestID}`} className={`text-xs ${s.muted}`}>Licenses from the next renewal</label>
                    <input id={`seats-${plan.RequestID}`} type="number" min="1" max={plan.Quantity} required
                           className={`${s.input} max-w-[6rem]`} value={seats} onChange={(e) => setSeats(e.target.value)} />
                    <button type="submit" className={s.btnSm} disabled={busy}>Save</button>
                    <button type="button" className={s.btnSm} onClick={() => setEditing(false)}>Cancel</button>
                    <span className={`basis-full text-xs ${s.muted}`}>
                        Fewer licenses start at the next renewal; you keep all {plan.Quantity} until then and aren't refunded. To add licenses, send a request below.
                    </span>
                </form>
            ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                    {!plan.CancelAtPeriodEnd && (
                        <button type="button" className={s.btnSm} disabled={busy} onClick={() => { setSeats(String(next)); setEditing(true); }}>
                            Use fewer licenses
                        </button>
                    )}
                    <button type="button" className={s.btnSm} disabled={busy} onClick={() => onAutoRenew(plan, !!plan.CancelAtPeriodEnd)}>
                        {plan.CancelAtPeriodEnd ? "Turn renewal back on" : "Stop renewing"}
                    </button>
                </div>
            )}
        </div>
    );
}
